import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useRecordContext } from 'react-admin';

import {
  AdminGetUserDatadogErrorsDocument,
  AdminGetUserDatadogErrorsQuery,
  AdminUserDetailsFragment,
} from '../../../../graphql/types';
import { dateAndTime } from '../../../../utils/date';
import { getGraphQLErrorDetails } from '../../../../utils/getGraphQLErrorDetails';

type DatadogErrors = NonNullable<
  NonNullable<AdminGetUserDatadogErrorsQuery['user']>['datadogErrors']
>;
type DatadogLog = DatadogErrors['logs'][number];
type LogPayload = Record<string, unknown>;

const isRecord = (value: unknown): value is LogPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/**
 * Datadog parses JSON logs into `attributes`, but our lambda lines arrive
 * prefixed with a timestamp and request id, so some are left unparsed with the
 * payload appended to the raw message. Returns null when neither yields an
 * object, so the caller can fall back to showing the message as-is.
 */
export const getLogPayload = (log: DatadogLog): LogPayload | null => {
  if (log.attributes && Object.keys(log.attributes).length > 0) {
    return log.attributes;
  }

  const jsonStart = log.message.indexOf('{');
  if (jsonStart === -1) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(log.message.slice(jsonStart));
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * The most useful one-line description available: an actual error message
 * beats the log's own `msg`, which is often just the logger's name.
 */
export const summarizeLogPayload = (payload: LogPayload): string | null => {
  if (Array.isArray(payload.errors)) {
    for (const entry of payload.errors) {
      if (isRecord(entry)) {
        const message = nonEmptyString(entry.message);
        if (message) {
          return message;
        }
      }
    }
  }

  return nonEmptyString(payload.msg) ?? nonEmptyString(payload.message);
};

export const describeLogContext = (payload: LogPayload): string | null => {
  const parts = [
    nonEmptyString(payload.graphqlOperationName),
    nonEmptyString(payload.errorType),
    nonEmptyString(payload.lambdaName),
  ].filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(' • ') : null;
};

const LogMessage: React.FC<{ log: DatadogLog }> = ({ log }) => {
  const [open, setOpen] = React.useState(false);
  const payload = getLogPayload(log);
  const summary = payload && summarizeLogPayload(payload);

  if (!payload || !summary) {
    return (
      <Typography fontSize={13} sx={{ overflowWrap: 'anywhere' }}>
        {log.message || '—'}
      </Typography>
    );
  }

  const context = describeLogContext(payload);

  return (
    <>
      <Typography fontSize={13} sx={{ overflowWrap: 'anywhere' }}>
        {summary}
      </Typography>
      {context && (
        <Typography variant="caption" color="text.secondary">
          {context}
        </Typography>
      )}
      <Box>
        <Link
          component="button"
          type="button"
          onClick={() => setOpen(!open)}
          fontSize={12}
        >
          {open ? 'Hide details' : 'Show details'}
        </Link>
      </Box>
      {open && (
        <Box
          component="pre"
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {JSON.stringify(payload, null, 2)}
        </Box>
      )}
    </>
  );
};

export const MemberDatadogErrorsTab: React.FC = () => {
  const record = useRecordContext<AdminUserDetailsFragment>();
  const { data, error, loading } = useQuery(AdminGetUserDatadogErrorsDocument, {
    variables: {
      userId: record!.id, // see skip
    },
    skip: !record?.id,
  });

  const datadogErrors = data?.user?.datadogErrors;
  const logs = datadogErrors?.logs ?? [];

  const { errorRequestId, errorPath } = getGraphQLErrorDetails(error);

  if (loading) {
    return <Typography>Loading errors from Datadog…</Typography>;
  }

  if (error) {
    return (
      <>
        <Typography color="error">
          An error occurred while fetching Datadog errors.
        </Typography>
        {errorPath && (
          <Typography color="error" fontSize={12}>
            Path: {errorPath}
          </Typography>
        )}
        {errorRequestId && (
          <Typography color="error" fontSize={12}>
            Request Id: {errorRequestId}
          </Typography>
        )}
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h6" fontSize={14}>
          Error-level logs from the last 7 days that reference this user.
        </Typography>
        {datadogErrors?.searchUrl && (
          <Link
            href={datadogErrors.searchUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View all in Datadog
          </Link>
        )}
      </Box>

      {/*
        The backend returns a reason instead of failing the query when Datadog
        can't be reached, so an empty table isn't mistaken for a healthy user.
      */}
      {datadogErrors?.unavailableReason && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {datadogErrors.unavailableReason}
        </Alert>
      )}

      {!datadogErrors?.unavailableReason && logs.length === 0 && (
        <Typography>No errors found in the last 7 days.</Typography>
      )}

      {logs.length > 0 && (
        <TableContainer sx={{ maxHeight: '90vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Datadog</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} sx={{ verticalAlign: 'top' }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dateAndTime(log.timestamp)}
                  </TableCell>
                  <TableCell>{log.status}</TableCell>
                  <TableCell>{log.service ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 700 }}>
                    <LogMessage log={log} />
                  </TableCell>
                  <TableCell>
                    <Link
                      href={log.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
